import React, { useEffect } from "react";

/**
 * Shared modal shell for the admin console.
 *
 * Every popup used to hand-roll its own fixed overlay with hardcoded
 * `backgroundColor: "white"` / `color: "#333"`, so they all looked slightly
 * different from each other and none of them followed the theme. This renders
 * the `.al-modal` shell instead: token-driven (works in dark mode), scrim click
 * and Escape both close, and the body scrolls while header/footer stay put.
 *
 * `size` sets the width — pick it from the popup's job: `sm` for a confirm or a
 * single field, `md` for a normal form, `lg` for forms with side-by-side
 * columns, `xl` for tables and galleries.
 */
const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  size = "md",
  footer,
  children,
  closeOnScrim = true,
  bodyClassName = "al-modal__body",
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    // Stop the page behind the modal from scrolling with it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={`al-modal al-modal--${size}`}
      onClick={closeOnScrim && onClose ? onClose : undefined}
    >
      <div className="al-modal__scrim" />
      <div
        className="al-modal__box"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal__head">
          <div style={{ minWidth: 0 }}>
            <h3 className="al-modal__title">{title}</h3>
            {subtitle && <p className="al-modal__sub">{subtitle}</p>}
          </div>
          {onClose && (
            <button
              type="button"
              className="al-act"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}
        </div>

        <div className={bodyClassName}>{children}</div>

        {footer && <div className="al-modal__foot">{footer}</div>}
      </div>
    </div>
  );
};

/** Label + control + error, matching the shared form styles. */
export const Field = ({ label, error, children, required }) => (
  <div className="al-field">
    {label && (
      <label className="al-label">
        {label}
        {required && <span style={{ color: "var(--color-danger-text)" }}> *</span>}
      </label>
    )}
    {children}
    {error && <span className="al-err">{error}</span>}
  </div>
);

/** Read-only fact shown inside a modal. */
export const KeyValue = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div className="al-kv__k">{label}</div>
    <div className="al-kv__v">{children}</div>
  </div>
);

export default Modal;
